// src/components/Category.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spinner, Alert, Button, Placeholder } from 'react-bootstrap';
import { FiMenu, FiX, FiArrowUpRight, FiSearch, FiMapPin, FiTag, FiCalendar } from 'react-icons/fi';
import { useTools } from '../components/ToolsContext';
import './Category.css';

function Category() {
    const { allTools, loading, error, loadProgress, isStreaming } = useTools();
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const ITEMS_PER_PAGE = 20;

    const categories = useMemo(() => {
        if (!allTools.length) return ['All'];
        const categorySet = new Set();
        allTools.forEach(tool => {
            const cat = tool.Category || tool.category || 'Uncategorized';
            categorySet.add(cat);
        });
        return ['All', ...Array.from(categorySet).sort()];
    }, [allTools]);

    const filteredTools = useMemo(() => {
        if (!allTools.length) return [];
        
        let tools = selectedCategory === 'All' 
            ? allTools 
            : allTools.filter(tool => {
                const toolCategory = tool.Category || tool.category || 'Uncategorized';
                return toolCategory === selectedCategory;
              });
        
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            tools = tools.filter(tool => {
                const name = tool.Name || tool.name || '';
                const desc = tool['Short Description'] || tool.description || '';
                return name.toLowerCase().includes(searchLower) ||
                       desc.toLowerCase().includes(searchLower);
            });
        }
        
        return tools;
    }, [allTools, selectedCategory, searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, searchQuery]);

    const totalPages = Math.ceil(filteredTools.length / ITEMS_PER_PAGE);
    const paginatedTools = filteredTools.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const isValidValue = (value) => {
        return value && value !== '' && value !== 0 && value !== '0' && value !== null && value !== undefined;
    };

    const getCategoryField = (tool) => {
        return tool.Category || tool.category || 'Uncategorized';
    };

    const getPaginationRange = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        range.forEach((i) => {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        });

        return rangeWithDots;
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // 🔥 Simple Skeleton Card Loader
    const SkeletonCard = () => (
        <Card className="category-tool-card">
            <Card.Body className="category-card-body">
                <div className="category-card-header">
                    <div className="category-logo-wrapper">
                        <Placeholder as="div" animation="wave">
                            <Placeholder style={{ width: '48px', height: '48px', borderRadius: '12px' }} />
                        </Placeholder>
                    </div>
                </div>
                <Placeholder as="h4" animation="wave" style={{ marginTop: '12px', marginBottom: '8px' }}>
                    <Placeholder xs={8} />
                </Placeholder>
                <Placeholder as="p" animation="wave" style={{ marginBottom: '16px' }}>
                    <Placeholder xs={12} />
                    <Placeholder xs={10} />
                    <Placeholder xs={8} />
                </Placeholder>
                <div className="category-tags-section">
                    <Placeholder as="span" animation="wave" style={{ display: 'inline-block', marginRight: '8px' }}>
                        <Placeholder style={{ width: '70px', height: '22px', borderRadius: '20px' }} />
                    </Placeholder>
                    <Placeholder as="span" animation="wave" style={{ display: 'inline-block', marginRight: '8px' }}>
                        <Placeholder style={{ width: '55px', height: '22px', borderRadius: '20px' }} />
                    </Placeholder>
                    <Placeholder as="span" animation="wave" style={{ display: 'inline-block' }}>
                        <Placeholder style={{ width: '65px', height: '22px', borderRadius: '20px' }} />
                    </Placeholder>
                </div>
                <div className="category-card-footer" style={{ marginTop: '16px' }}>
                    <div className="category-footer-left">
                        <Placeholder as="span" animation="wave" style={{ display: 'inline-block' }}>
                            <Placeholder style={{ width: '80px', height: '20px' }} />
                        </Placeholder>
                    </div>
                    <Placeholder.Button animation="wave" style={{ width: '70px', height: '32px', borderRadius: '8px' }} />
                </div>
            </Card.Body>
        </Card>
    );

    // Loading state - skeleton cards
    if (loading && allTools.length === 0) {
        return (
            <>
                <div className="category-header">
                    <div className="header-text">
                        <h1>🔍 Discover Powerful AI Tools by Category</h1>
                        <p>Supercharge your workflow with the best AI tools — all neatly organized for creators, developers & businesses.</p>
                        <div className="header-stats">
                            <span className="stat-badge">Loading tools...</span>
                        </div>
                    </div>
                </div>
                <div className="layout-wrapper">
                    <div className={`sidebar`}>
                        <div className="sidebar-header">
                            <h4>Categories</h4>
                        </div>
                        <div className="sidebar-content">
                            <ul>
                                {[...Array(6)].map((_, idx) => (
                                    <li key={idx}>
                                        <Placeholder as="span" animation="wave">
                                            <Placeholder xs={8} />
                                        </Placeholder>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="main-content">
                        <div className="tools-grid">
                            {[...Array(12)].map((_, idx) => (
                                <SkeletonCard key={idx} />
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error && allTools.length === 0) {
        return (
            <div className="text-center py-5" style={{ minHeight: '60vh' }}>
                <Alert variant="danger" className="mx-auto" style={{ maxWidth: '500px' }}>
                    <Alert.Heading>Error Loading Tools</Alert.Heading>
                    <p>{error}</p>
                    <Button variant="outline-danger" onClick={() => window.location.reload()}>
                        Try Again
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <>
            {isStreaming && allTools.length > 0 && (
                <div className="category-streaming-indicator">
                    <small>Loading more tools... ({allTools.length.toLocaleString()} loaded)</small>
                </div>
            )}

            <div className="category-header">
                <div className="header-text">
                    <h1>🔍 Discover Powerful AI Tools by Category</h1>
                    <p>Supercharge your workflow with the best AI tools — all neatly organized for creators, developers & businesses.</p>
                    <div className="header-stats">
                        <span className="stat-badge">{allTools.length} AI Tools Available</span>
                        <span className="stat-badge">{categories.length - 1} Categories</span>
                    </div>
                </div>
                <div className="header-image">
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/9799/9799789.png"
                        alt="AI Bot"
                    />
                </div>
            </div>

            <div className="layout-wrapper">
                <button 
                    className="mobile-menu-toggle" 
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                    {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                </button>

                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <h4>Categories</h4>
                        <span className="tools-count">{allTools.length} Total Tools</span>
                    </div>
                    <div className="sidebar-content">
                        <ul>
                            {categories.map((category, index) => {
                                const count = category === 'All'
                                    ? allTools.length
                                    : allTools.filter(tool => getCategoryField(tool) === category).length;
                                return (
                                    <li
                                        key={index}
                                        className={selectedCategory === category ? 'active' : ''}
                                        onClick={() => {
                                            setSelectedCategory(category);
                                            if (window.innerWidth <= 768) setSidebarOpen(false);
                                        }}
                                    >
                                        <span>{category}</span>
                                        <span className="category-count">{count}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                <div className="main-content">
                    <div className="content-header">
                        <h2>
                            {selectedCategory === 'All' ? 'All AI Tools' : selectedCategory}
                            <span className="tools-badge">{filteredTools.length} tools</span>
                        </h2>

                        <div className="search-wrapper">
                            <div className="search-box">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search tools by name or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => setShowSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                />
                                <FiSearch className="search-icon" />
                                {showSuggestions && searchQuery && filteredTools.length > 0 && (
                                    <div className="suggestions">
                                        <div className="suggestions-header">Quick Suggestions</div>
                                        {filteredTools.slice(0, 5).map((tool, index) => (
                                            <div
                                                key={index}
                                                className="suggestion-item"
                                                onClick={() => {
                                                    setSearchQuery(tool.Name || tool.name);
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                <FiSearch className="me-2" />
                                                {tool.Name || tool.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="results-count">
                        <span>
                            {filteredTools.length} {filteredTools.length === 1 ? 'Result' : 'Results'} Found
                            {searchQuery && ` for "${searchQuery}"`}
                            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                        </span>
                        {filteredTools.length > 0 && (
                            <span className="page-info">
                                Page {currentPage} of {totalPages}
                            </span>
                        )}
                    </div>

                    <div className="tools-grid">
                        {paginatedTools.length === 0 && !isStreaming ? (
                            <div className="no-tools">
                                <div className="no-tools-icon">🔍</div>
                                <h4>No tools found</h4>
                                <p>We couldn't find any tools matching your criteria.</p>
                                <button
                                    className="clear-filters-btn"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setSelectedCategory('All');
                                    }}
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        ) : paginatedTools.length > 0 ? (
                            paginatedTools.map((tool, index) => (
                                <Card key={index} className="category-tool-card">
                                    <Card.Body className="category-card-body">
                                        <div className="category-card-header">
                                            {tool.Logo && isValidValue(tool.Logo) && (
                                                <div className="category-logo-wrapper">
                                                    <img
                                                        src={tool.Logo}
                                                        alt={tool.Name || tool.name}
                                                        className="category-tool-logo"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://placehold.co/48x48?text=AI';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {typeof tool['Year Founded'] === 'number' &&
                                                tool['Year Founded'] > 0 && (
                                                    <div className="category-year-badge">
                                                        <FiCalendar size={12} />
                                                        <span>{tool['Year Founded']}</span>
                                                    </div>
                                                )}
                                        </div>

                                        <h4 className="category-tool-name">
                                            {tool.Name || tool.name || 'Tool Name'}
                                        </h4>

                                        <p className="category-tool-description">
                                            {tool['Short Description'] || tool.description || 'No description available.'}
                                        </p>

                                        <div className="category-tags-section">
                                            {tool.Category && isValidValue(tool.Category) && (
                                                <span className="category-tag category-tag-primary">
                                                    <FiTag size={10} />
                                                    #{tool.Category.toUpperCase()}
                                                </span>
                                            )}
                                            {tool['Primary Task'] && isValidValue(tool['Primary Task']) && (
                                                <span className="category-tag">
                                                    #{tool['Primary Task'].toUpperCase()}
                                                </span>
                                            )}
                                            {tool.industry && isValidValue(tool.industry) && (
                                                <span className="category-tag">
                                                    #{tool.industry.toUpperCase()}
                                                </span>
                                            )}
                                            {tool.technologies && isValidValue(tool.technologies) && (
                                                tool.technologies
                                                    .split(',')
                                                    .slice(0, 3)
                                                    .map((tech, idx) => (
                                                        <span key={idx} className="category-tag">
                                                            #{tech.trim()}
                                                        </span>
                                                    ))
                                            )}
                                        </div>

                                        {tool.Keywords && isValidValue(tool.Keywords) && (
                                            <div className="category-keywords">
                                                <span className="keywords-label">Keywords:</span>
                                                <span className="keywords-text">
                                                    {tool.Keywords.length > 150
                                                        ? tool.Keywords.substring(0, 150) + '...'
                                                        : tool.Keywords}
                                                </span>
                                            </div>
                                        )}

                                        <div className="category-card-footer">
                                            <div className="category-footer-left">
                                                {tool.Country && isValidValue(tool.Country) && (
                                                    <span className="category-country">
                                                        <FiMapPin size={12} />
                                                        {tool.Country}
                                                    </span>
                                                )}
                                                {tool['Website Status'] &&
                                                    tool['Website Status'] !== 'Error 403' &&
                                                    tool['Website Status'] !== '' && (
                                                        <span className={`category-status ${tool['Website Status'].toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`}>
                                                            {tool['Website Status']}
                                                        </span>
                                                    )}
                                            </div>
                                            <a
                                                href={tool.Website || tool.Link || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="category-visit-btn"
                                            >
                                                Visit <FiArrowUpRight size={14} />
                                            </a>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))
                        ) : isStreaming && paginatedTools.length === 0 ? (
                            [...Array(8)].map((_, idx) => <SkeletonCard key={idx} />)
                        ) : null}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                ← Previous
                            </button>
                            <div className="pagination-numbers">
                                {getPaginationRange().map((item, idx) => (
                                    item === '...' ? (
                                        <span key={idx} className="pagination-ellipsis">...</span>
                                    ) : (
                                        <button
                                            key={idx}
                                            className={`pagination-btn ${currentPage === item ? 'active' : ''}`}
                                            onClick={() => handlePageChange(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                ))}
                            </div>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    )}

                    {filteredTools.length > 0 && (
                        <div className="category-footer-info">
                            <p>
                                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                                {Math.min(currentPage * ITEMS_PER_PAGE, filteredTools.length)} of{' '}
                                {filteredTools.length} tools
                                {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Category;